import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Menu, MenuItem, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { blue, red } from '@mui/material/colors';
import { addMonths, endOfDay, format, isAfter, isBefore, startOfDay } from 'date-fns';
import Formsy from 'formsy-react';
import { List, Map } from 'immutable';
import _ from 'lodash';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import HorizontalDateBox from '@sophocles/components/HorizontalDateBox';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import { DataGridToolbarShell, ToolbarIconButton } from '@sophocles/data-grids/components/toolbar';
import {
  fetchDailyInternationalPowerMeterReportsForSiteWithRange,
  getDailyInternationalPowerMeterReportsForSelectedStructure,
} from '@sophocles/entities/DailyInternationalPowerMeterReport';
import {
  fetchMonthlyInternationalPowerMeterReportsForSiteWithRange,
  getMonthlyInternationalPowerMeterReportsForSelectedStructure,
} from '@sophocles/entities/MonthlyInternationalPowerMeterReport';
import {
  fetchWeeklyInternationalPowerMeterReportsForSiteWithRange,
  getWeeklyInternationalPowerMeterReportsForSelectedStructure,
} from '@sophocles/entities/WeeklyInternationalPowerMeterReport';
import useDateRangeSearchParams from '@sophocles/hooks/useDateRangeSearchParams';
import PowerChart from '@sophocles/views/sites/components/PowerConsumptionChart';
import { useSiteDashboardLayoutContext } from '@sophocles/views/sites/SiteDashboardLayout';

import PowerConsumptionGrid from './components/PowerConsumptionGrid';
import TicketLinksHeader from './components/TicketLinksHeader';

export function PowerConsumptionViewPage(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const { user } = useUser();
  const { selectedStructure, loading: dashboardLoading } = useSiteDashboardLayoutContext();
  const selectedStructureId = params.site_id;

  const dailyReports = useSelector(getDailyInternationalPowerMeterReportsForSelectedStructure);
  const weeklyReports = useSelector(getWeeklyInternationalPowerMeterReportsForSelectedStructure);
  const monthlyReports = useSelector(getMonthlyInternationalPowerMeterReportsForSelectedStructure);

  const [tab, setTab] = useState('daily');
  const [anchorEl, setAnchorEl] = useState(null);

  const defaultStart = useMemo(() => addMonths(new Date(), -2), []);
  const defaultEnd = useMemo(() => new Date(), []);
  const { startDate, endDate, onDateRangeChange } = useDateRangeSearchParams({
    defaultStart,
    defaultEnd,
  });

  const fetchDataForTab = useCallback(
    (which, start, end) => {
      if (selectedStructureId == null) return;
      const from = format(start, 'yyyy-MM-dd');
      const to = format(end, 'yyyy-MM-dd');
      let action = null;
      if (which === 'daily')
        action = fetchDailyInternationalPowerMeterReportsForSiteWithRange(selectedStructureId, from, to);
      else if (which === 'weekly')
        action = fetchWeeklyInternationalPowerMeterReportsForSiteWithRange(selectedStructureId, from, to);
      else if (which === 'monthly')
        action = fetchMonthlyInternationalPowerMeterReportsForSiteWithRange(selectedStructureId, from, to);
      if (!action) return;
      dispatch(action);
    },
    [dispatch, selectedStructureId],
  );

  useEffect(() => {
    fetchDataForTab(tab, startDate, endDate);
  }, [fetchDataForTab, tab, startDate, endDate]);

  const startDateFilter = useCallback(
    date => {
      if (typeof date !== 'string') onDateRangeChange(date, endDate);
    },
    [onDateRangeChange, endDate],
  );
  const endDateFilter = useCallback(
    date => {
      if (typeof date !== 'string') onDateRangeChange(startDate, date);
    },
    [onDateRangeChange, startDate],
  );
  const menuClick = useCallback(
    (e, url) => {
      e.preventDefault();
      navigate(url);
    },
    [navigate],
  );

  if (dashboardLoading) return <LoadingScreen />;

  const isStaff = user.get('is_staff', false);
  const unitId = selectedStructure.getIn(['relay_controller', 'id'], 0);
  const unit = selectedStructure.get('relay_controller', Map());
  const measuresPower = unit.get('measures_power_consumption_in_watts', false);
  const measuresComplexPower = unit.getIn(['model', 'measures_complex_ac_power'], false);
  const powerOrCurrent = measuresPower ? t('powerData.power', 'Power') : t('powerData.current', 'Current');

  const startDateTime = startOfDay(startDate);
  const endDateTime = endOfDay(endDate);

  let dataSource = List();
  let chartTitle = '';
  if (tab === 'daily') {
    chartTitle = t('powerData.dailyConsumptionReadings');
    dataSource = dailyReports;
  } else if (tab === 'weekly') {
    chartTitle = t('powerData.weeklyConsumptionReadings');
    dataSource = weeklyReports;
  } else if (tab === 'monthly') {
    chartTitle = t('powerData.monthlyConsumptionReadings');
    dataSource = monthlyReports;
  }

  const dataList = dataSource
    .filter(data => {
      const m = data.get('report_date');
      return isAfter(m, startDateTime) && isBefore(m, endDateTime);
    })
    .map(data =>
      data.merge({
        consumed_energy: _.round(data.get('consumed_energy', 0), 3),
        short: format(data.get('moment', new Date()), 'MM/dd'),
      }),
    )
    .sortBy(item => item.get('moment'))
    .reverse();

  const plainList = dataList.toJS();
  _.reverse(plainList);

  const dateStartParam = searchParams.get('date_start');
  const dateEndParam = searchParams.get('date_end');
  const dateParams = dateStartParam && dateEndParam ? `?date_end=${dateEndParam}&date_start=${dateStartParam}` : '';

  const menuOpen = Boolean(anchorEl);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        paddingBottom: 'var(--page-py)',
      }}
    >
      <TicketLinksHeader params={params} isStaff={isStaff} />
      {unitId != 0 ? (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <DataGridToolbarShell>
            <Formsy className="form-inline" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <HorizontalDateBox
                title={t('sites.startDate', 'Start Date')}
                name="start_date"
                value={startDate}
                labelClassName="no-bottom-margin pad-right"
                inputDivClassName="inline"
                inputClassName="form-control form-control-sm"
                outerClassName="form-group"
                onChange={startDateFilter}
              />
              <HorizontalDateBox
                title={t('sites.endDate', 'End Date')}
                name="end_date"
                value={endDate}
                labelClassName="no-bottom-margin pad-right"
                inputDivClassName="inline"
                inputClassName="form-control form-control-sm"
                outerClassName="form-group"
                rightAlign
                onChange={endDateFilter}
                isValidDate={current => isBefore(current, new Date())}
              />
            </Formsy>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              {!measuresComplexPower ? (
                <Typography variant="caption" sx={{ color: red[500] }}>
                  {t('powerData.noSupportForFeature')}
                </Typography>
              ) : null}
              <ToggleButtonGroup
                value={tab}
                exclusive
                size="small"
                onChange={(_e, val) => {
                  if (val) setTab(val);
                }}
              >
                <ToggleButton value="daily">{t('general.daily')}</ToggleButton>
                <ToggleButton value="weekly">{t('general.weekly')}</ToggleButton>
                <ToggleButton value="monthly">{t('general.monthly')}</ToggleButton>
              </ToggleButtonGroup>
              {measuresComplexPower || isStaff ? (
                <Fragment>
                  <ToolbarIconButton
                    tooltip={t('general.more', 'More')}
                    onClick={event => setAnchorEl(event.currentTarget)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </ToolbarIconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClick={() => setAnchorEl(null)}
                    onClose={() => setAnchorEl(null)}
                  >
                    <MenuItem
                      onClick={e =>
                        menuClick(
                          e,
                          `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/readings/power${dateParams}`,
                        )
                      }
                    >
                      {`${powerOrCurrent} ${t('powerData.readings')}`}
                    </MenuItem>
                    <MenuItem
                      sx={{ color: blue[500] }}
                      onClick={e =>
                        menuClick(
                          e,
                          `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/readings/power/consumption${dateParams}`,
                        )
                      }
                    >
                      {t('pages.powerConsumption')}
                    </MenuItem>
                    {isStaff ? (
                      <MenuItem
                        sx={measuresComplexPower ? null : { color: red[500] }}
                        onClick={e =>
                          menuClick(
                            e,
                            `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/readings/power/meter${dateParams}`,
                          )
                        }
                      >
                        {t('pages.internationalMeterReadings')}
                      </MenuItem>
                    ) : null}
                  </Menu>
                </Fragment>
              ) : null}
            </Box>
          </DataGridToolbarShell>
          <Typography variant="h6" align="center">
            {chartTitle}
          </Typography>
          <PowerChart list={plainList} />
          <Box sx={{ height: 400, mt: 1 }}>
            <PowerConsumptionGrid list={dataList} />
          </Box>
        </Box>
      ) : (
        <Typography variant="h5" align="center" sx={{ mt: 4 }}>
          {t('powerData.noReadings')}
        </Typography>
      )}
    </Box>
  );
}

export default PowerConsumptionViewPage;
